
import {
  computed, defineComponent, onMounted, PropType, reactive, Ref, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { usePrimeVue } from 'primevue/config';
import DataTable, { DataTableFilterMeta, DataTableFilterMetaData } from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import Message from 'primevue/message';
import { DataTableEvent, SpmTableFilter } from '@/types';
import {
  SpmTableColumns,
  SpmTableSort,
  SpmTableState,
  SpmTableControlDescriptor,
  SpmTableControlConfig,
} from '@/types/table-types';
import { FilterOperatorsEnum, SortOrderEnum } from '@/types/enums';
import { getEnumKeyByStringValue, showToastError, showToastSuccess } from '@/helpers';
import { ListSettings, SaveResult } from '@/composables/GraphQL';
import Loader from '@/components/layout/Loader.vue';
import Checkbox from 'primevue/checkbox';
import Dropdown from 'primevue/dropdown';
import Menu from 'primevue/menu';
import Button from 'primevue/button';
import { useConfirm } from 'primevue/useconfirm';
import {
  ActionType,
  DatatablesExportsInputItem,
  ResolverType,
  OperatorType,
} from '@/types/generated-types/graphql';
import InsertDatatablesExport from '@/composables/datatables-export/HandleCron';
import ActionModal from '@/components/table/ActionModal.vue';
import ConfirmDialog from 'primevue/confirmdialog';
import Tooltip from 'primevue/tooltip';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import { hasAccessToFeatures, UserState } from '@/composables/User';
import { useStore } from '@/store';
import { MenuItem } from 'primevue/menuitem';
import SecureSpmButton from '@/components/spm-primevue/SecureSpmButton.vue';
import { NestApiType, nestCall, toQueryString } from '@/composables/nestApi';
import Filter from './Filter.vue';
import SpmTableControlsPanel from './SpmTableControlsPanel.vue';
import Renderer from './Renderer.vue';

const updateValue = 'update:modelValue';

export default defineComponent({
  name: 'SpmTableApi',
  components: {
    SecureSpmButton,
    ActionModal,
    Loader,
    DataTable,
    Column,
    InputText,
    Message,
    Filter,
    Renderer,
    Checkbox,
    Dropdown,
    Button,
    Menu,
    ConfirmDialog,
    SpmButton,
    SpmTableControlsPanel,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    modelValue: {
      type: Object as PropType<SpmTableState>,
      required: false,
      default: (): SpmTableState => ({
        items: [],
        total: 0,
        error: '',
        isLoading: true,
        selectedItems: [],
      }),
    },

    typeApi: {
      type: String,
      required: true,
    },

    route: {
      type: String,
      required: true,
    },

    name: {
      type: String,
      required: true,
    },

    index: {
      type: String,
      required: true,
    },

    isGlobalLoading: {
      type: Boolean,
      required: false,
      default: false,
    },

    summary: {
      type: String,
      default: '',
      required: false,
    },

    tableColumns: {
      type: Array as PropType<Array<SpmTableColumns>>,
      required: true,
    },

    useExternalControls: {
      type: Boolean,
      required: false,
      default: true,
    },

    limit: {
      type: Number,
      required: false,
      default: 10,
    },

    persistentFilters: {
      type: Array as PropType<Array<SpmTableFilter>>,
      required: false,
      default: () => [],
    },

    initialSorting: {
      type: Array as PropType<Array<SpmTableSort>>,
      required: false,
      default: () => [],
    },

    selectionMode: {
      type: String,
      required: false,
      default: '',
    },

    expandRows: {
      type: Boolean,
      required: false,
      default: false,
    },

    paginator: {
      type: Boolean,
      required: false,
      default: true,
    },

    customSelector: {
      type: Boolean,
      required: false,
      default: false,
    },

    idShop: {
      type: Number,
      required: false,
      default: 0,
    },

    groupedActionsOptions: {
      type: Array as PropType<MenuItem[]>,
      required: false,
      default: () => [],
    },

    groupedActionsKey: {
      type: String,
      required: false,
      default: '',
    },

    showExportMenuItem: {
      type: Boolean,
      required: false,
      default: false,
    },

    reorderableRows: {
      type: Boolean,
      required: false,
      default: false,
    },

    scrollable: {
      type: Boolean,
      required: false,
      default: false,
    },

    dynamicConfiguration: {
      type: Boolean,
      required: false,
      default: false,
    },

    reloadDataTable: {
      type: Boolean,
      required: false,
      default: true,
    },

    excludeName: {
      type: String,
      required: false,
      default: '',
    },

    excludeField: {
      type: String,
      required: false,
      default: '',
    },

    excludeFilters: {
      type: Array as PropType<Array<SpmTableFilter>>,
      required: false,
      default: () => [],
    },

    exportFileName: {
      type: String,
      required: false,
      default: '',
    },

    customSettings: {
      type: Object as PropType<any>,
      required: false,
      default: () => ({}),
    },
  },

  emits: [
    updateValue,
    'cell-edit-init',
    'cell-edit-complete',
    'row-selection-complete',
    'row-reorder',
    'reload-data',
    'data-loaded',
    'filters-change',
  ],

  setup(props, context) {
    const {
      t,
      locale,
    } = useI18n();
    const store = useStore();
    const uniqueIdS6Generator = () => Math.random().toString(36).substr(2, 6);
    const confirmDialogGroup = ref(`group_${uniqueIdS6Generator()}`);

    const freeTableExport: any = ref(['ShopsCustomersManage', 'ShopsPushToken'].includes(props.name));

    const menu = ref();
    const confirm = useConfirm();
    const showCustomDialog = ref(false);
    const unselectedRows: Ref<number[]> = ref([]);
    const clearEvent = ref(false);
    const mounting = ref(true);
    const dialogData: Ref<{
      message: string;
      header: string;
      icon: string;
      acceptLabel: string;
      rejectLabel: string;
      actionType: string;
      idCustomer: number| null;
      idUser: number|null;
      listName?: string;
      customFunction?: Function;
    }> = ref({
      message: '',
      header: '',
      icon: '',
      acceptLabel: '',
      rejectLabel: '',
      actionType: '',
      idCustomer: null,
      idUser: null,
      listName: undefined,
      customFunction: undefined,
    });
    const spmTableKey = ref(0);

    const groupSelection = ref<string|null>(null);
    const rows = ref();

    const selection = [
      {
        code: 'unselect',
        label: t('bulkActions.selection.unselect'),
      },
      {
        code: 'visible',
        label: t('bulkActions.selection.visible'),
      },
      {
        code: 'all',
        label: t('bulkActions.selection.all'),
      },
    ];
    const idShopList = ref<string|null>(null);

    const getFullFieldName = (field?: string, prefix?: string): string => {
      if (!field) {
        return '';
      }

      if (field.includes('.')) {
        return field;
      }

      return prefix ? `${prefix}.${field}` : field;
    };

    const resolveFieldWithPrefix = (field?: string, prefix?: string): string | null => {
      if (!field) {
        return null;
      }

      return getFullFieldName(field, prefix);
    };

    const buildControlColumn = (
      column: SpmTableColumns,
      control: SpmTableControlConfig,
    ): SpmTableColumns => {
      const resolvedField = control.filterField ?? control.field ?? column.field;
      const resolvedPrefix = control.prefix ?? column.prefix;

      const derivedColumn: SpmTableColumns = {
        ...column,
        field: resolvedField ?? column.field,
        prefix: resolvedPrefix,
        header: control.label ?? column.header,
        sortable: control.sortable ?? column.sortable ?? Boolean(control.sortField),
        filterable: control.filterable ?? column.filterable ?? false,
        type: control.type ?? column.type,
        filterSettings: control.filterSettings ?? column.filterSettings,
        matchModes: control.matchModes ?? column.matchModes,
        filterValue: control.filterValue ?? column.filterValue,
        translatable: control.translatable ?? column.translatable,
        hidden: true,
      };

      (derivedColumn as any).controls = undefined;

      return derivedColumn;
    };

    const getFilterKeyFromColumn = (column: SpmTableColumns): string => getFullFieldName(column.field, column.prefix);

    const buildBaseDescriptor = (column: SpmTableColumns): SpmTableControlDescriptor | null => {
      const filterKey = getFilterKeyFromColumn(column);
      const supportsFiltering = Boolean(column.filterable);
      const supportsSorting = Boolean(column.sortable);

      if (!supportsFiltering && !supportsSorting) {
        return null;
      }

      return {
        key: `base-${filterKey}`,
        column,
        label: column.header,
        filterKey,
        supportsFiltering,
        supportsSorting,
        sortField: supportsSorting ? filterKey : null,
        displayField: column.field,
      };
    };

    const buildControlDescriptor = (
      column: SpmTableColumns,
      control: SpmTableControlConfig,
      index: number,
    ): SpmTableControlDescriptor | null => {
      const derivedColumn = buildControlColumn(column, control);
      const filterKey = getFilterKeyFromColumn(derivedColumn);
      const supportsFiltering = Boolean(derivedColumn.filterable);
      const rawSortField = control.sortField ?? control.field ?? derivedColumn.field;
      const sortFieldCandidate = resolveFieldWithPrefix(rawSortField, control.prefix ?? column.prefix);
      const supportsSorting = Boolean(control.sortable ?? column.sortable ?? control.sortField) && Boolean(sortFieldCandidate);

      if (!supportsFiltering && !supportsSorting) {
        return null;
      }

      return {
        key: control.key ?? `${filterKey}-${index}`,
        column: derivedColumn,
        label: control.label ?? column.header,
        filterKey,
        supportsFiltering,
        supportsSorting,
        sortField: supportsSorting ? sortFieldCandidate : null,
        displayField: column.field,
      };
    };

    const filterColumns = computed(() => {
      const map = new Map<string, SpmTableColumns>();

      props.tableColumns.forEach((column) => {
        if (column.filterable) {
          map.set(getFilterKeyFromColumn(column), column);
        }

        if (column.controls && column.controls.length) {
          column.controls.forEach((control) => {
            const derivedColumn = buildControlColumn(column, control);

            if (derivedColumn.filterable) {
              map.set(getFilterKeyFromColumn(derivedColumn), derivedColumn);
            }
          });
        }
      });

      return Array.from(map.values());
    });

    const tableControls = computed<SpmTableControlDescriptor[]>(() => {
      const descriptors: SpmTableControlDescriptor[] = [];

      props.tableColumns.forEach((column) => {
        const baseDescriptor = buildBaseDescriptor(column);

        if (baseDescriptor) {
          descriptors.push(baseDescriptor);
        }

        if (column.controls && column.controls.length) {
          column.controls.forEach((control, index) => {
            const descriptor = buildControlDescriptor(column, control, index);

            if (descriptor) {
              descriptors.push(descriptor);
            }
          });
        }
      });

      return descriptors;
    });

    const controlByFilterKey = computed(() => {
      const map = new Map<string, SpmTableControlDescriptor>();

      tableControls.value.forEach((descriptor) => {
        if (descriptor.supportsFiltering) {
          map.set(descriptor.filterKey, descriptor);
        }
      });

      return map;
    });

    const hasControlsPanel = computed(() => props.useExternalControls && tableControls.value.length > 0);
    const controlsPanelVisible = ref(false);

    watch(hasControlsPanel, (available) => {
      if (!available) {
        controlsPanelVisible.value = false;
      }
    });

    watch(() => props.useExternalControls, (value) => {
      if (!value) {
        controlsPanelVisible.value = false;
      }
    });

    interface ControlsApplyPayload {
      filters: Record<string, any>;
      sort: SpmTableSort | null;
    }

    const resolveMatchMode = (column: SpmTableColumns): string => {
      let matchMode = column.filterSettings?.defaultMatchMode ?? 'contains';

      if (column.matchModes && column.matchModes.length) {
        matchMode = column.matchModes[0]?.value ?? matchMode;
      } else if (column.filterSettings) {
        const rawType = column.filterSettings.type;
        const filterType = typeof rawType === 'string'
          ? rawType.toLowerCase()
          : '';

        if (filterType === 'date') {
          matchMode = 'interval';
        } else if (
          [
            'multiselect',
            'multi-select',
            'multi_select',
            'language',
            'country',
            'dropdown',
            'select',
            'single-select',
            'singleselect',
            'single_select',
          ].includes(filterType)
        ) {
          matchMode = 'in';
        }
      }

      return matchMode;
    };

    const normalizeFilterValueFromColumn = (
      column: SpmTableColumns,
      matchMode: string,
      previousValue: unknown,
    ): unknown => {
      let value = previousValue;
      const columnFilterValue: any = (column as any).filterValue;

      if (columnFilterValue) {
        if (
          Object.prototype.hasOwnProperty.call(columnFilterValue, 'values')
          && columnFilterValue.values !== undefined
          && columnFilterValue.values !== null
        ) {
          value = columnFilterValue.values;
        } else if (
          Object.prototype.hasOwnProperty.call(columnFilterValue, 'value')
          && columnFilterValue.value !== undefined
          && columnFilterValue.value !== null
        ) {
          value = columnFilterValue.value;
        }
      }

      if (value === undefined || value === null || value === '') {
        return value ?? '';
      }

      if (matchMode === 'interval') {
        return value;
      }

      if (matchMode === 'in' || matchMode === 'notIn') {
        if (Array.isArray(value)) {
          return value;
        }

        if (typeof value === 'string') {
          return value
            .split(',')
            .map((entry) => entry.trim())
            .filter((entry) => entry.length > 0);
        }
      }

      return value;
    };

    const buildFilterParams = (
      columns: SpmTableColumns[],
      previousParams: Record<string, any> = {},
    ): Record<string, any> => columns
      .filter((column) => column.filterable)
      .reduce<Record<string, any>>((acc, current) => {
        const key = current.prefix ? `${current.prefix}.${current.field}` : current.field;
        const matchMode = resolveMatchMode(current);
        const previousValue = previousParams[key]?.constraints?.[0]?.value;
        const normalizedValue = normalizeFilterValueFromColumn(current, matchMode, previousValue);

        acc[key] = {
          operator: previousParams[key]?.operator ?? null,
          constraints: [{ value: normalizedValue ?? '', matchMode }],
        };

        return acc;
      }, {});

    const filterParams = ref<Record<string, any>>(buildFilterParams(filterColumns.value));

    watch(
      filterColumns,
      (columns: SpmTableColumns[]) => {
        filterParams.value = buildFilterParams(columns, filterParams.value);
      },
      { deep: true },
    );

    const showFiltersRow = computed(() => !props.useExternalControls && props.tableColumns
      .filter((column) => column.filterable)
      .length > 0);

    const toggle = (event: Event) => {
      menu.value.toggle(event);
    };

    const sendSelectedRows = () => {
      context.emit('row-selection-complete', rows.value);
      rows.value = [];
    };

    const settings = reactive<ListSettings>({
      limit: props.limit,
      offset: 0,
      order: [...props.initialSorting],
      filter: [...props.persistentFilters],
    });

    const paginatorTemplate: Ref<string> = ref('FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown');

    const getHeaders = () => props.tableColumns.filter((column) => !column.custom && !column.hidden)
      .map((column) => ({ [column.field]: column.header }));

    const toggleDialog = (listId: string, message: string, header: string, icon: string, acceptLabel: string, rejectLabel: string,
      actionType: string, customerId: number| null, listName?: string, customFunction?: Function) => {
      if ((rows.value === undefined || rows.value.length === 0) && !customerId && !listId) {
        return showToastError(t('bulkActions.noData'));
      }

      idShopList.value = listId === '' ? null : listId;

      showCustomDialog.value = true;

      dialogData.value = {
        header,
        message,
        icon,
        acceptLabel,
        rejectLabel,
        actionType,
        idCustomer: customerId,
        listName,
        idUser: UserState.user?.id,
        customFunction,
      };

      return true;
    };

    const exportConfirmDialog = async (exportAll = true) => {
      const accessExport = hasAccessToFeatures('data_csv.export');
      if (!exportAll) {
        if (groupSelection.value === null && (rows.value === undefined || rows.value.length === 0)) {
          return showToastError(t('bulkActions.cron.errorSelect'));
        }
        if (groupSelection.value !== ActionType.All && (rows.value === undefined || rows.value.length === 0)) {
          return showToastError(t('bulkActions.cron.errorSelect'));
        }
      }

      const exportConfirm = () => {
        confirm.require({
          group: confirmDialogGroup.value,
          message: t('myLists.manage.bulkActions.export.text'),
          header: t('myLists.manage.bulkActions.export.formTitle'),
          icon: 'far fa-info-circle',
          acceptLabel: t('myLists.manage.bulkActions.export.acceptLabel'),
          rejectLabel: t('myLists.manage.bulkActions.export.rejectLabel'),
          accept: async () => {
            let actionType: ActionType| null = null;
            let concernedIds: number[] = [];

            if (!exportAll) {
              if (groupSelection.value === ActionType.All) {
                actionType = ActionType.All;
                concernedIds = unselectedRows.value;
              } else {
                actionType = ActionType.Select;
                concernedIds = rows.value;
              }
            } else {
              actionType = ActionType.All;
              concernedIds = [];
            }

            // settings for stats are different
            const finalSettings = Object.entries(props.customSettings).length === 0
              ? buildEffectiveSettings()
              : props.customSettings;
            const fileName = props.summary ? `${props.exportFileName}_${props.summary}` : props.exportFileName;
            const resolverName = props.name === 'StatsTableGet' ? props.name : `${props.name}List`;
            const result: SaveResult<DatatablesExportsInputItem> = await InsertDatatablesExport(props.idShop,
              finalSettings, resolverName, getHeaders(), fileName, locale.value, ResolverType.Query,
              concernedIds, actionType, props.index);

            if (result.err) {
              return showToastError(t('bulkActions.cron.error'));
            }
            return showToastSuccess(t('bulkActions.cron.success'));
          },
          reject: () => {
            // callback to execute when user rejects the action
          },
        });
      };

      if (!freeTableExport.value && accessExport && !accessExport.access) {
        store.commit('general/setIsFeatureUnavailableModalVisible', true);
        store.commit('general/setFeatureUnavailableMinPlan', accessExport.minimumPlan);
        store.commit('general/setOnAuthorizedClickFunction', exportConfirm);
        return true;
      }

      return exportConfirm();
    };
    const groupedActions = ref(props.groupedActionsOptions);

    if (props.showExportMenuItem) {
      groupedActions.value.push({
        icon: !freeTableExport.value ? 'fas fa-star' : '',
        label: t('myLists.manage.bulkActions.export.menuItem'),
        command: async () => {
          await exportConfirmDialog(false);
        },
      });
    }

    const value = reactive<SpmTableState>({ ...props.modelValue });
    const selectedRows = ref([]); // list of selected rows
    const expandedRows = ref([]); // list of expanded rows

    const selectAllRows = () => {
      if (groupSelection.value === 'unselect') {
        rows.value = [];
        unselectedRows.value = [];
      }
      if (groupSelection.value === 'all') {
        unselectedRows.value = [];
        rows.value = value.items.map((u: any) => u[props.groupedActionsKey]);
      }
      if (groupSelection.value === 'visible') {
        rows.value = value.items.map((u: any) => u[props.groupedActionsKey]);
      }
    };

    const unselectAllRows = () => {
      rows.value = [];
      unselectedRows.value = [];
    };

    const onCellEditInit: Function = (event: any) => {
      const {
        data,
        newValue,
        field,
      } = event;

      if (newValue !== '' && newValue) {
        data[field] = newValue;
      }
      context.emit('cell-edit-init', field, data, event);
    };

    const onCellEditingComplete: Function = (event: any) => {
      const {
        data,
        newValue,
        field,
      } = event;

      if (newValue !== '') {
        data[field] = newValue;
        context.emit('cell-edit-complete', field, data);
      }
    };

    const rowClicked = (event: any) => {
      if (groupSelection.value === 'all' && unselectedRows.value) {
        // eslint-disable-next-line no-underscore-dangle
        const { id } = event.target.__vueParentComponent.attrs;
        if (event.target.childElementCount === 0) {
          unselectedRows.value.push(id);
        } else if (unselectedRows.value.indexOf(id) > -1) {
          unselectedRows.value = unselectedRows.value.filter((e) => e !== id);
        }
      }
    };

    watch(() => props.modelValue, (v: SpmTableState) => {
      value.items = v.items;
      value.total = v.total;
      value.error = v.error;
      value.isLoading = v.isLoading;
      value.selectedItems = selectedRows.value;
      rows.value = v.selectedItems;

      paginatorTemplate.value = value.total <= settings.limit ? 'CurrentPageReport RowsPerPageDropdown'
        : 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown';
    }, { deep: true });

    watch(selectedRows, () => {
      const updatedValue: SpmTableState = {
        items: value.items,
        total: value.total,
        error: value.error,
        isLoading: value.isLoading,
        selectedItems: selectedRows.value,
      };

      context.emit(updateValue, updatedValue);
    });

    const columns = computed(() => props.tableColumns.filter((c) => !c.hidden));

    const translatePrimeVueFilters = () => {
      const primevue = usePrimeVue();
      primevue.config.locale.startsWith = t('filters.startsWith');
      primevue.config.locale.contains = t('filters.contains');
      primevue.config.locale.notContains = t('filters.notContains');
      primevue.config.locale.endsWith = t('filters.endsWith');
      primevue.config.locale.equals = t('filters.equals');
      primevue.config.locale.notEquals = t('filters.notEquals');
      primevue.config.locale.noFilter = t('filters.noFilter');
    };

    onMounted(translatePrimeVueFilters);

    const filterClientSideTableData = () => {
      value.items = props.modelValue.items.filter((item: any) => Object.entries(filterParams.value).every(([column, filter]) => {
        const { matchMode: filterMatchMode, value: filterConstraintValue } = filter.constraints[0];
        const columnValue = String(item[column]);
        if (
          filterConstraintValue === ''
          || (Array.isArray(filterConstraintValue) && filterConstraintValue.length === 0)
        ) return true;

        if (filterMatchMode === FilterOperatorsEnum.NOT_CONTAINS) {
          return !columnValue.includes(filterConstraintValue);
        }

        if (filterMatchMode === FilterOperatorsEnum.STARTS_WITH) {
          return columnValue.startsWith(filterConstraintValue);
        }

        if (filterMatchMode === FilterOperatorsEnum.ENDS_WITH) {
          return columnValue.endsWith(filterConstraintValue);
        }

        if (filterMatchMode === FilterOperatorsEnum.EQUALS) {
          return columnValue === filterConstraintValue;
        }

        if (filterMatchMode === FilterOperatorsEnum.NOT_EQUALS) {
          return columnValue !== filterConstraintValue;
        }

        if (filterMatchMode === FilterOperatorsEnum.IN) {
          if (Array.isArray(filterConstraintValue) && filterConstraintValue.length) {
            return filterConstraintValue.join(',').includes(columnValue);
          }
          return filterConstraintValue.includes(columnValue);
        }

        return columnValue.includes(filterConstraintValue);
      }));
      value.total = value.items.length;
    };

    const clearClientSideTableData = (filterCallback: Function, field: string) => {
      // filterParams.value[field].constraints[0].value = '';
      removeFromFilterParams(field);
      removeFromActiveFilters(field);

      filterClientSideTableData();
      filterCallback();
    };

    const isIntervalOperator = (operator: unknown) => typeof operator === 'string' && operator.toUpperCase() === 'INTERVAL';

    const hasIntervalStructure = (value: any) => {
      if (!value || typeof value !== 'object') {
        return false;
      }

      if (Array.isArray(value)) {
        return value.some((entry) => entry && typeof entry === 'object' && 'interval' in entry);
      }

      return 'interval' in value;
    };

    const isIntervalValueEmpty = (value: any) => {
      if (value === undefined || value === null) {
        return true;
      }

      if (Array.isArray(value)) {
        return value.length < 2 || !value[0] || !value[1];
      }

      if (typeof value !== 'object') {
        return value === '';
      }

      const interval = value.interval;

      if (interval === undefined || interval === null || interval === 'empty') {
        return true;
      }

      if (interval === 'customDateRange') {
        const range = value.customDateRange;

        return !Array.isArray(range) || range.length < 2 || !range[0] || !range[1];
      }

      return false;
    };

    const sanitizeIntervalFilters = (entries: any[]) => {
      if (!Array.isArray(entries) || entries.length === 0) {
        return entries;
      }

      const filteredEntries = entries.filter((element: any) => {
        const candidate = element?.values ?? element?.value;

        if (!isIntervalOperator(element?.operator) && !hasIntervalStructure(candidate)) {
          return true;
        }

        return !isIntervalValueEmpty(candidate);
      });

      return filteredEntries.length === entries.length ? entries : filteredEntries;
    };

    const buildEffectiveSettings = (): ListSettings => ({
      ...settings,
      filter: sanitizeIntervalFilters(settings.filter),
    });

    const extractResponseItems = (response: any) => {
      if (Array.isArray(response?.data)) {
        return response.data;
      }

      if (Array.isArray(response?.items)) {
        return response.items;
      }

      return [];
    };

    const extractExcludedItems = (response: any) => {
      if (Array.isArray(response?.items)) {
        return response.items;
      }

      if (Array.isArray(response?.data)) {
        return response.data;
      }

      return [];
    };

    const extractResponseTotal = (response: any, fallbackLength: number) => {
      if (typeof response?.total === 'number') {
        return response.total;
      }

      const metaTotal = response?.meta?.pagination?.total_items;
      if (typeof metaTotal === 'number') {
        return metaTotal;
      }

      return fallbackLength;
    };

    const fetchData = async () => {
      const effectiveSettings = buildEffectiveSettings();

      if (props.dynamicConfiguration) {
        value.items = [];
        value.total = 0;
        value.isLoading = true;
        context.emit('reload-data', effectiveSettings);
        return;
      }

      // if no name provided which means it's a client side datatable so we cant reload the data
      // Or if a name and the value of modelValue is provided which means It's still a client side dataTable
      // and the name is provided because of the export functionality
      if (!props.name || (props.name && props.modelValue.total !== 0)) {
        if (props.modelValue.selectedItems) {
          rows.value = props.modelValue.selectedItems;
        }
        return;
      }

      value.isLoading = true;
      if (props.name !== '') {
        const normalizedSettings = {
          ...effectiveSettings,
          order: settings.order
            ? settings.order.map((sort) => ({
              ...sort,
              type: typeof sort.type === 'string' ? sort.type.toLowerCase() : sort.type,
            }))
            : settings.order,
        };

        const query = toQueryString({
          settings: JSON.stringify(normalizedSettings),
          fields: props.tableColumns.filter((column) => !column.custom)
            .map((column) => column.field),
        });

        const res = await nestCall('get', props.typeApi as NestApiType, props.route, {}, `?${query}`, {});

        let responseItems = extractResponseItems(res);

        // If the list need to be filtered with another table
        if (props.excludeName && res && res.err === '' && responseItems.length) {
          const toIncludes = responseItems.map((item: any) => item[props.excludeField]).join(',');
          const newFilters = [...props.excludeFilters];
          newFilters.push({ field: props.excludeField, value: toIncludes, operator: OperatorType.In });

          const normalizedExcludeSettings = {
            limit: 0,
            offset: 0,
            order: [],
            filter: newFilters,
          };

          const queryFilter = toQueryString({
            settings: JSON.stringify(normalizedExcludeSettings),
            fields: [props.excludeField],
          });

          const excludedLists = await nestCall('get', props.typeApi as NestApiType, props.route, {}, `?${queryFilter}`, {}, false);

          const excludedItems = extractExcludedItems(excludedLists);

          if (excludedLists && excludedLists.err === '' && excludedItems.length > 0) {
            responseItems = responseItems.filter((item: any) => {
              if (excludedItems.find((itemList: any) => itemList[props.excludeField] === item[props.excludeField])) {
                return true;
              }
              return false;
            });
          }
        }

        if (settings.offset && settings.offset > 0) {
          if (groupSelection.value === 'all') {
            rows.value = [...rows.value, ...responseItems.map((u: any) => u[props.groupedActionsKey])];
          }
        }

        const updatedValue: SpmTableState = {
          items: responseItems,
          total: extractResponseTotal(res, responseItems.length),
          error: res.err,
          selectedItems: selectedRows.value,
          isLoading: false,
        };

        value.items = updatedValue.items;
        value.total = updatedValue.total;
        value.error = updatedValue.error;
        value.selectedItems = updatedValue.selectedItems;
        value.isLoading = updatedValue.isLoading;

        paginatorTemplate.value = value.total <= settings.limit ? 'CurrentPageReport RowsPerPageDropdown'
          : 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown';

        context.emit(updateValue, updatedValue);
        context.emit('data-loaded', updatedValue.items);
      } else {
        value.isLoading = false;
      }
    };

    const reloadData = async () => {
      await fetchData();
      spmTableKey.value += 1;
    };

    const removeFromFilterParams = (field: string) => {
      Object.entries(filterParams.value).forEach(([key, filterParam]) => {
        if (key.includes(field)) {
          // eslint-disable-next-line no-param-reassign
          filterParam.constraints[0].value = '';
        }
      });
    };

    const activeFilters = ref<Set<string>>(new Set());

    

    const hasActiveConstraintValue = (constraint?: DataTableFilterMetaData | null) => {
      if (!constraint) {
        return false;
      }

      const { value } = constraint;

      if (constraint.matchMode === 'interval') {
        return !isIntervalValueEmpty(value);
      }

      if (Array.isArray(value)) {
        return value.length > 0;
      }

      return value !== '' && value !== undefined && value !== null;
    };

    const getFilterStatus = (field: string) => (activeFilters.value.has(field) ? 'column-filter-active' : '');

    const removeFromActiveFilters = (field: string) => {
      activeFilters.value.delete(field);
    };

    const syncActiveFiltersFromMeta = (filtersMeta: Record<string, any>) => {
      const newActiveFilters = new Set<string>();

      Object.entries(filtersMeta).forEach(([key, parameters]) => {
        const constraint = parameters?.constraints?.[0];

        if (hasActiveConstraintValue(constraint)) {
          const descriptor = controlByFilterKey.value.get(key);
          if (descriptor?.displayField) {
            newActiveFilters.add(descriptor.displayField);
          } else if (descriptor?.column.field) {
            newActiveFilters.add(descriptor.column.field);
          } else {
            newActiveFilters.add(key.includes('.') ? key.substring(key.lastIndexOf('.') + 1) : key);
          }
        }
      });

      activeFilters.value = newActiveFilters;
    };

    const clearFilter = (filterValue: any, filterCallback: Function, field: any) => {
      clearEvent.value = true;

      if (props.name === '') {
        clearClientSideTableData(filterCallback, field);
        return;
      }

      // find the index in the filter by the field and the value
      const index = settings.filter.findIndex((filter) => {
        const resultingStr = filter.field.replace(/^[^.]+?\./, '');

        return resultingStr === field;
      });
      // remove the filter from the settings
      if (index !== -1) {
        settings.filter.splice(index, 1);
      }

      // add the filters which may have been deleted from the persistent filters again
      // speeding the lookup through a map
      const map = new Map(settings.filter.map((obj) => [JSON.stringify(obj), obj]));

      // Iterate over persistentFilters and add items that are not in  settings.filter or differ
      // eslint-disable-next-line no-restricted-syntax
      for (const obj of props.persistentFilters) {
        if (!map.has(JSON.stringify(obj)) || (map.get(JSON.stringify(obj)) && map.get(JSON.stringify(obj))?.value !== obj.value)) {
          settings.filter.push(obj);
        }
      }

      removeFromFilterParams(field);
      removeFromActiveFilters(field);
      reloadData();
      filterCallback();
    };

    const applyFilter = (filterValue: any, filterCallback: Function, field: string) => {
      if (props.name === '') {
        filterClientSideTableData();
      }

      const constraint = filterValue.constraints[0] as DataTableFilterMetaData;
      if (hasActiveConstraintValue(constraint)) {
        activeFilters.value.add(field);
      } else {
        clearFilter(filterValue, filterCallback, field);
        return;
      }

      filterCallback();
    };

    const handleControlsApply = ({ filters, sort }: ControlsApplyPayload) => {
      filterParams.value = { ...filters };

      syncActiveFiltersFromMeta(filterParams.value);

      if (props.name === '') {
        filterClientSideTableData();
      }

      if (sort && sort.field) {
        settings.order = [sort];
      } else {
        settings.order = [...props.initialSorting];
      }

      onFilterChange(filterParams.value as DataTableFilterMeta);
    };

    const handleControlsReset = () => {
      const defaultFilters = buildFilterParams(filterColumns.value);

      filterParams.value = defaultFilters;
      syncActiveFiltersFromMeta(filterParams.value);

      settings.filter = [...props.persistentFilters];
      settings.offset = 0;
      settings.order = [...props.initialSorting];

      if (props.name === '') {
        filterClientSideTableData();
      }

      onFilterChange(filterParams.value as DataTableFilterMeta);
    };

    const onPageChange = (event: DataTableEvent) => {
      settings.limit = event.rows;
      settings.offset = event.first;
    };

    const onSortChange = (event: DataTableEvent) => {
      const sortList: SpmTableSort = {
        field: event.sortField,
        type: event.sortOrder < 0 ? SortOrderEnum.DESC : SortOrderEnum.ASC,
      };

      settings.order = [sortList];
    };

    const onFilterChange = (filterMeta: DataTableFilterMeta) => {
      settings.offset = 0;

      if (clearEvent.value) {
        context.emit('filters-change', buildEffectiveSettings());
        clearEvent.value = false;
        return;
      }

      // extracting filtering parameters with user's input
      const filteredParams = Object.entries(filterParams.value)
        .filter(([, parameters]) => hasActiveConstraintValue(parameters.constraints[0] as DataTableFilterMetaData))
        .map(([column, parameters]) => {
          let finalValue = parameters.constraints[0].value;

          if (parameters.constraints[0].matchMode === 'interval') {
            finalValue = parameters.constraints[0].value.interval === 'customDateRange' ? parameters.constraints[0].value.customDateRange
              : parameters.constraints[0].value;
            return {
              field: column,
              values: finalValue,
              operator: getEnumKeyByStringValue(FilterOperatorsEnum, parameters.constraints[0].matchMode),
            };
          }

          if (parameters.constraints[0].matchMode === 'in' || parameters.constraints[0].matchMode === 'notIn') {
            return {
              field: column,
              values: finalValue.join(','),
              operator: getEnumKeyByStringValue(FilterOperatorsEnum, parameters.constraints[0].matchMode),
            };
          }

          return {
            field: column,
            value: finalValue,
            operator: getEnumKeyByStringValue(FilterOperatorsEnum, parameters.constraints[0].matchMode),
          };
        });
      const newPersistentFilters = props.persistentFilters.filter((filter1) => !filteredParams.some((filter2) => filter1.field === filter2.field));
      settings.filter = [...newPersistentFilters, ...filteredParams];

      syncActiveFiltersFromMeta(filterParams.value);

      context.emit('filters-change', buildEffectiveSettings());
    };

    context.expose({
      toggleDialog,
      exportConfirmDialog,
      sendSelectedRows,
      reloadData,
      unselectAllRows,
    });

    const searchByFilter = () => {
      settings.offset = 0;
      settings.filter = [...props.persistentFilters];
    };

    watch(() => settings, () => {
      fetchData();
    }, { deep: true });
    watch(() => props.persistentFilters, searchByFilter, { deep: true });

    const lazyLoading = computed(() => props.name !== '' || (props.name && props.modelValue.total !== 0)
      || props.dynamicConfiguration);

    const onRowReorder = (event: any) => {
      context.emit('row-reorder', event.value);
    };

    onMounted(async () => {
      await fetchData();
      mounting.value = false;
    });

    return {
      value,
      columns,
      selectedRows,
      expandedRows,
      filterParams,
      onSortChange,
      onPageChange,
      onFilterChange,
      selection,
      onCellEditingComplete,
      props,
      t,
      groupSelection,
      selectAllRows,
      rows,
      rowClicked,
      toggle,
      menu,
      showCustomDialog,
      dialogData,
      settings,
      buildEffectiveSettings,
      unselectedRows,
      idShopList,
      reloadData,
      onRowReorder,
      showFiltersRow,
      paginatorTemplate,
      lazyLoading,
      groupedActions,
      onCellEditInit,
      clearFilter,
      applyFilter,
      getFilterStatus,
      spmTableKey,
      exportConfirmDialog,
      confirmDialogGroup,
      freeTableExport,
      hasControlsPanel,
      tableControls,
      controlsPanelVisible,
      handleControlsApply,
      handleControlsReset,
    };
  },
});
